import { useEffect, useState } from 'react';
import { styled, useTheme, alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import CssBaseline from '@mui/material/CssBaseline';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import InboxIcon from '@mui/icons-material/MoveToInbox';
import AccountCircle from '@mui/icons-material/AccountCircle';
import DepartureBoardIcon from '@mui/icons-material/DepartureBoard';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SearchIcon from '@mui/icons-material/Search';
import HomeIcon from '@mui/icons-material/Home';
import MailIcon from '@mui/icons-material/Mail';
import InputBase from '@mui/material/InputBase';
import { Route, Switch, Link } from 'react-router-dom';
import AddWorker from '../worker/AddWorker';
import EditWorker from '../worker/EditWorker';
import Home from '../home/Home';
import WorkerList from '../worker/WorkerList';
import ContractList from '../contracts/ContractList';
import AddContract from '../contracts/AddContract';
import EditContract from '../contracts/EditContract';
import AddDepartment from '../department/AddDepartment';
import EditDepartment from '../department/EditDepartment';
import DepartmentList from '../department/DepartmentList';
import AddBranch from '../branch/AddBranch';
import EditBranch from '../branch/EditBranch';
import BranchList from '../branch/BranchList';
import globals from '../../services/globals';
import loginService from '../../services/loginService.js';
import SalariesList from './../salaries/SalariesList';
import EditSalary from './../salaries/EditSalary';
import AddSalary from './../salaries/AddSalary';
import Autocomplete from '@mui/material/Autocomplete';
import MonthSalariesList from '../salaries/MonthSalariesList';
import AddList from '../salaries/AddList';
import AddWorkerContract from '../worker/AddWorkerContract';
import EditWorkerContract from '../worker/EditWorkerContract';
import ArticleIcon from '@mui/icons-material/Article';
import UpdateIcon from '@mui/icons-material/Update';
import FiscalPoliticsList from '../fiscal-politics/FiscalPoliticsList';
import AddFiscalPolitic from '../fiscal-politics/AddFiscalPolitic';
import EditFiscalPolitic from '../fiscal-politics/EditFiscalPolitics';
import TemplateList from '../contracts-templates/TemplateList';
import AddTemplate from '../contracts-templates/AddTemplate';
import EditTemplate from '../contracts-templates/EditTemplate';
import Reports from '../report/Reports';
import CreditorMonthlyReport from '../report/CreditorMonthlyReport';
import HolidaysList from '../holidays/HolidaysList';
import AddHoliday from '../holidays/AddHoliday';
import EditHoliday from '../holidays/EditHoliday';
import EmployeeCard from '../report/EmployeeCard';
import EmployeesList from '../report/EmployeesList';
import Company from '../company/Company';
const drawerWidth = 240;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(9)} + 1px)`,
  },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));
const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginRight: theme.spacing(2),
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(3),
    width: 'auto',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: '20ch',
    },
  },
}));

const list = (anchor) => (
  <Box
    sx={{ width: anchor === 'top' || anchor === 'bottom' ? 'auto' : 250 }}
    role="presentation"
  // onClick={handleDrawerClose()}
  // onKeyDown={handleDrawerClose()}
  >
    <List>
      <Link to="/departmentlist">
        <ListItem button key={'Department'}>
          <ListItemIcon>
            <DepartureBoardIcon />
          </ListItemIcon>
          <ListItemText primary={'Department'} />
        </ListItem>
      </Link>
    </List>
    <Divider />
    <List>
      <Link to="/branchlist">
        <ListItem button key={'Branch'}>
          <ListItemIcon>
            <BookmarkAddIcon />
          </ListItemIcon>
          <ListItemText primary={'Branch'} />
        </ListItem>
      </Link>
      <Link to="/fiscal">
        <ListItem button key={'Fiscal'}>
          <ListItemIcon>
            <AccountBalanceIcon />
          </ListItemIcon>
          <ListItemText primary={'Politikat Fiskale'} />
        </ListItem>
      </Link>
      <Link to="/contracts-templates">
        <ListItem button key={'Kontrata'}>
          <ListItemIcon>
            <ArticleIcon />
          </ListItemIcon>
          <ListItemText primary={'Kontrata'} />
        </ListItem>
      </Link>
        <ListItem>
          <ListItemIcon>
            <UpdateIcon />
          </ListItemIcon>
          <ListItemText primary={'version 1.0.0.20'} />
        </ListItem>
    </List>
    <Divider />
    {/* <List>
      {['All mail', 'Trash', 'Spam'].map((text, index) => (
        <ListItem button key={text}>
          <ListItemIcon>
            {index % 2 === 0 ? <InboxIcon /> : <MailIcon />}
          </ListItemIcon>
          <ListItemText primary={text} />
        </ListItem>
      ))}
    </List> */}
  </Box>
);
export default function Dashboard() {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [logout, setLogout] = useState();
  const [anchorEl, setAnchorEl] = useState(null);
  useEffect(() => {
    setLogout(globals().getLogedInUser());
    //console.log(globals().getLogedInUser());
  }, []);
  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleLogout = () => {
    //e.preventDefault();
    loginService().logout();
  };
  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar position="fixed" open={open} style={{ background: '#4175df' }}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{
              marginRight: '36px',
              ...(open && { display: 'none' }),
            }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            T3 HR
          </Typography>

          <Search>
            <SearchIconWrapper>
              <SearchIcon />
            </SearchIconWrapper>
            <StyledInputBase
              placeholder="Search…"
              inputProps={{ 'aria-label': 'search' }}
            />
          </Search>

          <Link to="/">
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              color="inherit"
            >
              <HomeIcon />
            </IconButton>
          </Link>
          <IconButton
            size="large"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleMenu}
            color="inherit"
          >
            <AccountCircle />
          </IconButton>
          <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            <MenuItem onClick={handleClose}>Profile</MenuItem>
            <MenuItem onClick={handleClose}>
              <Link to="/company" >Company</Link>
            </MenuItem>
            <MenuItem onClick={handleLogout}>Logout</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Drawer variant="permanent" open={open}>
        <DrawerHeader>
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === 'rtl' ? (
              <ChevronRightIcon />
            ) : (
              <ChevronLeftIcon />
            )}
          </IconButton>
        </DrawerHeader>
        <Divider />
        {list('right')}
        <Divider />
      </Drawer>

      <Box sx={{ flexGrow: 1, p: 3 }}>
        <DrawerHeader />
        <Switch>
          <Route exact path="/">
            <Home />
          </Route>
          <Route path="/workerlist/addworkercontract/:id">
            <AddWorkerContract />
          </Route>
          <Route path="/workerlist/editworkercontract/:id/:con">
            <EditWorkerContract />
          </Route>
          <Route path="/workerlist/editworkercontract/:id">
            <EditWorkerContract />
          </Route>

          <Route path="/workerlist/addworker">
            <AddWorker />
          </Route>
          <Route path="/workerlist/editworker/:id">
            <EditWorker />
          </Route>
          <Route path="/workerlist" component={WorkerList} />


          <Route path="/contractlist/addcontract">
            <AddContract />
          </Route>
          <Route path="/contractlist/editcontract/:id">
            <EditContract />
          </Route>
          <Route path="/contractlist" component={ContractList} />

          <Route path="/salarieslist/addsalary">
            <AddSalary />
          </Route>
          <Route path="/salarieslist/addlist">
            <AddList />
          </Route>
          <Route path="/salarieslist/editlist/:id">
            <AddList />
          </Route>
          <Route path="/salarieslist/editsalary/:id">
            <EditSalary />
          </Route>
          <Route path="/salarieslist/monthlist/:year/:month">
            <MonthSalariesList />
          </Route>
          <Route path="/salarieslist" component={SalariesList} />

          <Route path="/departmentlist/adddepartment">
            <AddDepartment />
          </Route>
          <Route path="/departmentlist/editdepartment/:id">
            <EditDepartment />
          </Route>
          <Route path="/departmentlist" component={DepartmentList} />

          <Route path="/branchlist/addbranch">
            <AddBranch />
          </Route>
          <Route path="/branchlist/editbranch/:id">
            <EditBranch />
          </Route>
          <Route path="/branchlist" component={BranchList} />

          <Route path="/fiscal/addfiscal" >
            <AddFiscalPolitic />
          </Route>

          <Route path="/fiscal/editfiscal/:id" >
            <EditFiscalPolitic />
          </Route>
          <Route path="/fiscal" component={FiscalPoliticsList} />

          <Route path="/contracts-templates/addtemplate">
            <AddTemplate />
          </Route>
          <Route path="/contracts-templates/edittemplate/:id">
            <EditTemplate />
          </Route>
          <Route path="/contracts-templates" component={TemplateList} />

          <Route path="/home/reports/creditor-monthly" component={CreditorMonthlyReport} />
          <Route path="/home/reports/employee-card/:id" component={EmployeeCard} />
          <Route path="/home/reports/employees-cards" component={EmployeesList} />
          <Route path="/home/reports" component={Reports} />

          <Route path="/holidays/addholiday" >
            <AddHoliday />
          </Route>
          <Route path="/holidays/editholiday/:id" >
            <EditHoliday />
          </Route>
          <Route path="/holidays" component={HolidaysList} />

          <Route path="/company">
            <Company />
          </Route>
        </Switch>
      </Box>
    </Box>
  );
}
